import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Box,
  Clock3,
  FileText,
  Loader2,
  PlayCircle,
} from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";

const getArrayFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.mockTests)) return result.mockTests;
  if (Array.isArray(data?.mockTests)) return data.mockTests;
  if (Array.isArray(data)) return data;

  return [];
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getCategory = (mockTest) =>
  pickFirst(
    mockTest?.category,
    mockTest?.course?.planName,
    mockTest?.courseName,
    mockTest?.examName,
    mockTest?.subject,
    "Mock Test",
  );

const getMockTestId = (mockTest) =>
  pickFirst(
    mockTest?.mockTest?._id,
    mockTest?.mockTestId?._id,
    mockTest?.mockTestId,
    mockTest?.testId?._id,
    mockTest?.testId,
    mockTest?._id,
  );

const getIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return pickFirst(value?._id, value?.id, value?.examId, "");
  }

  return value;
};

const getSelectedExamId = (userData) => {
  const selectedExam = pickFirst(
    getIdValue(userData?.selectedExam),
    getIdValue(userData?.exam),
    userData?.examId,
    localStorage.getItem("examId"),
  );

  return selectedExam ? String(selectedExam) : "";
};

const MockTestsAll = () => {
  const navigate = useNavigate();
  const [mockTests, setMockTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingMockTestId, setStartingMockTestId] = useState("");
  const [hasSelectedExam, setHasSelectedExam] = useState(true);
  const [hasPurchased, setHasPurchased] = useState(false);

  useEffect(() => {
    const fetchMockTests = async () => {
      setLoading(true);

      try {
        let userData = null;

        try {
          const userResponse = await axios.get("me");
          userData = userResponse?.data?.result;
        } catch {
          userData = null;
        }

        setHasPurchased(Boolean(userData && userData?.planName !== "Test"));

        const examId = getSelectedExamId(userData);

        setHasSelectedExam(Boolean(examId));

        if (!examId) {
          setMockTests([]);
          return;
        }

        const response = await axios.get(`/exams/${examId}/mock-tests`);
        setMockTests(getArrayFromResponse(response));
      } catch (error) {
        toast.error("Unable to load mock tests");
        setMockTests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMockTests();
  }, []);

  const totalTests = useMemo(() => mockTests.length, [mockTests]);

  const handleStartTest = async (mockTest) => {
    if (!hasPurchased) {
      toast.info("Please purchase a plan to start mock test");
      navigate("/dash/plan");
      return;
    }

    const mockTestId = getMockTestId(mockTest);

    if (!mockTestId) {
      toast.error("Mock test is not available");
      return;
    }

    try {
      setStartingMockTestId(String(mockTestId));
      await axios.post(`/mock-tests/${mockTestId}/start`);
      navigate(`/dash/mock-test/${mockTestId}?start=${mockTestId}`);
    } catch (error) {
      toast.error("Unable to start mock test");
      setStartingMockTestId("");
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      <main className="mx-auto lg:mt-10 max-w-6xl px-4 py-6 sm:px-6 lg:px-12">
        <section className="flex max-w-[620px] items-center gap-4 rounded-[1.35rem] bg-[#562566] px-4 py-5 text-left text-white shadow-[0_12px_24px_rgba(86,37,102,0.16)] lg:max-w-[560px] lg:px-5 lg:py-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/14">
            <Box className="h-7 w-7" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold leading-tight sm:text-xl">
              Selected Exam Mock Tests
            </h2>
            <p className="mt-1 max-w-md text-xs font-medium leading-5 text-white/82 sm:text-sm">
              Practice tests available for your selected exam.
            </p>
          </div>
        </section>

        {loading ? (
          <div className="mt-6 flex min-h-[260px] items-center justify-center rounded-[1.6rem] border border-[#eee8f0] bg-white">
            <Loader2 className="h-9 w-9 animate-spin text-[#562566]" />
          </div>
        ) : totalTests ? (
          <section className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {mockTests.map((mockTest, index) => {
              const mockTestId = getMockTestId(mockTest) || index;
              const title = pickFirst(
                mockTest?.title,
                mockTest?.name,
                mockTest?.mockTestName,
                "Mock Test",
              );
              const description = pickFirst(
                mockTest?.description,
                mockTest?.subTitle,
                title,
              );
              const totalQuestions = pickFirst(
                mockTest?.totalQuestions,
                mockTest?.questionCount,
                mockTest?.questions?.length,
                0,
              );
              const duration = pickFirst(
                mockTest?.durationMinutes,
                mockTest?.duration,
                mockTest?.timeLimit,
                0,
              );
              const marks = pickFirst(
                mockTest?.totalMarks,
                mockTest?.marks,
                mockTest?.maxMarks,
                totalQuestions,
              );
              const detailTarget = `/dash/mock-test-details/${mockTestId}`;
              const isStarting =
                startingMockTestId === String(getMockTestId(mockTest));

              return (
                <article
                  key={mockTestId}
                  className="relative overflow-hidden rounded-[1.45rem] border border-[#ebe4ef] bg-white p-5 pl-5 text-left shadow-[0_8px_22px_rgba(33,22,41,0.10)] xl:p-4 xl:pl-4"
                >
                  <div className="absolute inset-y-0 left-0 w-2 bg-[#562566]" />

                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="break-words text-xl font-semibold leading-tight text-[#211629] xl:text-lg">
                        {title}
                      </h3>
                      <p className="mt-2 text-sm font-medium leading-6 text-[#6e6875] xl:text-xs">
                        {description}
                      </p>
                    </div>

                    <span className="shrink-0 rounded-full bg-[#eee9ef] px-5 py-3 text-base font-bold text-[#562566] xl:px-4 xl:py-2 xl:text-sm">
                      {getCategory(mockTest)}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <span className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full bg-[#f6eef9] px-2 py-2 text-xs font-semibold text-[#562566] sm:gap-2 sm:px-4 sm:py-3 sm:text-base xl:px-2 xl:py-2 xl:text-xs">
                      <FileText className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                      {totalQuestions} Questions
                    </span>
                    <span className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full bg-[#f6eef9] px-2 py-2 text-xs font-semibold text-[#562566] sm:gap-2 sm:px-4 sm:py-3 sm:text-base xl:px-2 xl:py-2 xl:text-xs">
                      <Clock3 className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                      {duration} min
                    </span>
                    <span className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full bg-[#f6eef9] px-2 py-2 text-xs font-semibold text-[#562566] sm:gap-2 sm:px-4 sm:py-3 sm:text-base xl:px-2 xl:py-2 xl:text-xs">
                      <Award className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                      {marks} Marks
                    </span>
                  </div>

                  <div className="mt-4 w-full">
                    <div className="grid w-full grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(detailTarget)}
                        className="inline-flex h-12 w-full min-w-0 items-center justify-center gap-1.5 rounded-full border border-[#eaddef] bg-white px-3 text-sm font-extrabold text-[#562566] transition hover:bg-[#f8f1fa] xl:h-10 xl:text-xs"
                      >
                        Details
                        <ArrowRight className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStartTest(mockTest)}
                        disabled={isStarting}
                        className="inline-flex h-12 w-full min-w-0 items-center justify-center gap-1.5 rounded-full bg-[#F5A623] px-3 text-sm font-extrabold text-white shadow-[0_12px_22px_rgba(245,166,35,0.30)] transition hover:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 xl:h-10 xl:text-xs"
                      >
                        {isStarting
                          ? "Starting"
                          : hasPurchased
                            ? "Start Test"
                            : "Buy Plan"}
                        {isStarting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <PlayCircle className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        ) : (
          <div className="mt-6 rounded-[1.6rem] border border-[#eee8f0] bg-white px-5 py-10 text-center shadow-sm">
            <h2 className="text-xl font-extrabold text-[#211629]">
              {hasSelectedExam ? "No mock tests available" : "Please select an exam"}
            </h2>
            <p className="mt-2 text-sm font-medium text-gray-500">
              {hasSelectedExam
                ? "No mock tests found for your selected exam."
                : "Select your exam first to view matching mock tests."}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default MockTestsAll;
